import { useApolloClient } from "@apollo/client";
import * as Sentry from "@sentry/react";
import { useAnalyticsContext } from "contexts/AnalyticsContext";
import { useAppDispatch } from "hooks/utilsHooks/storeHooks";
import { useCallback } from "react";
import { useIdleTimerContext } from "react-idle-timer";
import { batch } from "react-redux";
import { resetAppState, setAppState } from "slices/appStateSlice";
import { refetchDishTypePrices } from "slices/dishesSlice";
import { finishOrderState } from "slices/transactionSlice";
import { AppState } from "types/mainTypes";

type AwsOrderUpdate = {
	finishOrder: () => void;
};

export const useOrder = (): AwsOrderUpdate => {
	const dispatch = useAppDispatch();
	const idleTimer = useIdleTimerContext();
	const analyticsContext = useAnalyticsContext();
	const apolloClient = useApolloClient();

	const finishOrder = useCallback(() => {
		try {
			// Order completion counts as fresh customer presence: wake the fridge back to ACTIVE and
			// re-arm the inactive-timeout countdown so the lights dim only after a FULL inactive timeout of
			// post-order inactivity — never the instant the order clears. `start()` restores initial state
			// AND restarts the timer (react-idle-timer); `reset()` did not wake presence, and `activate()`
			// only emits onActive when already idle and left the timer not firing `idle` again. The explicit
			// setAppState(ACTIVE) wakes the lighting converger immediately, independent of any idle-timer
			// callback. Without this, an order that ends while the customer was tablet-idle (the timeout
			// fired during the order, but an active transaction forbade StandBy) would flip
			// computeDesiredLighting to StandBy the instant the order clears and dim immediately.
			idleTimer.start();
			analyticsContext.reset();
			batch(() => {
				dispatch(finishOrderState());
				dispatch(resetAppState());
				dispatch(setAppState(AppState.ACTIVE));
			});
			dispatch(refetchDishTypePrices(apolloClient));
			return true;
		} catch (error) {
			Sentry.captureException(new Error(`Error while finishing order: ${error}`));
			return false;
		}
	}, [idleTimer, analyticsContext, dispatch, apolloClient]);

	return {
		finishOrder: finishOrder,
	};
};
